import * as React from 'react';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { cn } from '@repo/utils';
import { Button } from './Button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { useDebounce } from '../../lib/hooks';

export interface EntityLookupItem {
  id: string;
  code?: string;
  title: string;
  subtitle?: string;
  badge?: string;
  metadata?: Record<string, unknown>;
}

export interface EntityComboboxProps {
  value?: string;
  onChange: (value: string, item?: EntityLookupItem) => void;
  items?: EntityLookupItem[];
  fetchItems?: (query: string) => Promise<EntityLookupItem[]>;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  renderItem?: (item: EntityLookupItem, isSelected: boolean) => React.ReactNode;
}

export function EntityCombobox({
  value,
  onChange,
  items: staticItems,
  fetchItems,
  placeholder = 'Select entity...',
  searchPlaceholder = 'Search...',
  emptyText = 'No items found.',
  disabled = false,
  className,
  renderItem,
}: EntityComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [asyncItems, setAsyncItems] = React.useState<EntityLookupItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<EntityLookupItem | null>(null);

  // Fetch items when query changes (if fetchItems provided)
  React.useEffect(() => {
    if (!fetchItems) return;
    let active = true;
    setLoading(true);
    fetchItems(debouncedSearch)
      .then((res) => {
        if (active) {
          setAsyncItems(res || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setAsyncItems([]);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [fetchItems, debouncedSearch]);

  const items = fetchItems ? asyncItems : staticItems || [];

  // Update selected item label cache
  React.useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    const match = items.find((i) => i.id === value);
    if (match) {
      setSelectedItem(match);
    }
  }, [value, items]);

  const displayLabel = selectedItem
    ? selectedItem.code
      ? `${selectedItem.title} (${selectedItem.code})`
      : selectedItem.title
    : value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('relative flex items-center w-full', className)}>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-surface-0 border border-surface-3 font-normal text-left px-3 py-2 text-sm h-10"
            disabled={disabled}
          >
            {value ? (
              <span className="truncate text-text-primary font-medium">{displayLabel}</span>
            ) : (
              <span className="text-text-muted">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 text-text-secondary" />
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onChange('', undefined);
              setSelectedItem(null);
            }}
            className="absolute right-8 p-1 h-6 w-6 rounded-full hover:bg-surface-2 text-text-muted"
            title="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-dropdown" align="start">
        <Command shouldFilter={!fetchItems} className="flex flex-col max-h-72">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-surface-1">
            <Search className="h-4 w-4 text-text-muted shrink-0" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none h-8"
            />
          </div>
          <Command.List className="max-h-60 overflow-y-auto p-1">
            <Command.Empty className="py-4 text-center text-sm text-text-muted">
              {loading ? 'Searching...' : emptyText}
            </Command.Empty>
            {items.map((item) => {
              const isSelected = value === item.id;
              return (
                <Command.Item
                  key={item.id}
                  value={`${item.id} ${item.title} ${item.code || ''}`}
                  onSelect={() => {
                    onChange(item.id, item);
                    setSelectedItem(item);
                    setOpen(false);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(item.id, item);
                    setSelectedItem(item);
                    setOpen(false);
                  }}
                  className={cn(
                    'relative flex cursor-pointer select-none items-center justify-between rounded-md px-2.5 py-2 text-sm outline-none transition-colors',
                    'hover:bg-surface-2 aria-selected:bg-accent-500/10 aria-selected:text-accent-500',
                  )}
                >
                  {renderItem ? (
                    renderItem(item, isSelected)
                  ) : (
                    <div className="flex flex-col gap-0.5 min-w-0 pr-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary truncate">{item.title}</span>
                        {item.code && (
                          <span className="font-mono text-xs text-text-secondary bg-surface-2 px-1.5 py-0.5 rounded">
                            {item.code}
                          </span>
                        )}
                        {item.badge && (
                          <span className="text-[10px] font-semibold text-accent-500 bg-accent-500/10 px-1.5 py-0.5 rounded uppercase">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      {item.subtitle && (
                        <span className="text-xs text-text-muted truncate">{item.subtitle}</span>
                      )}
                    </div>
                  )}
                  <Check
                    className={cn(
                      'h-4 w-4 shrink-0 text-accent-500',
                      isSelected ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </Command.Item>
              );
            })}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
